/**
 * CreateStaffModal — invite a new staff user (+ optional first preset bundle / ownership). Delegates to
 * the lab-admin createStaff endpoint (which forwards to the iam service: bcrypt policy, preset validation,
 * the one-actif advisory lock). Client-side validation is a convenience; the server is authoritative.
 */
import { useState } from "react";
import { Button, FormField, Input, Modal, useToast } from "@/ui";
import { ApiError } from "@/api";
import { useT } from "@/app";
import { useCreateStaff } from "../api";
import { PresetPicker } from "./PresetPicker";

export interface CreateStaffModalProps {
  open: boolean;
  onClose: () => void;
  labId: string | null;
}

export function CreateStaffModal({ open, onClose, labId }: CreateStaffModalProps) {
  const { t } = useT();
  const toast = useToast();
  const create = useCreateStaff(labId);

  const [nom, setNom] = useState("");
  const [prenom, setPrenom] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [presets, setPresets] = useState<string[]>([]);
  const [owner, setOwner] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setNom("");
    setPrenom("");
    setEmail("");
    setPassword("");
    setPresets([]);
    setOwner(false);
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = () => {
    setError(null);
    if (!nom.trim() || !prenom.trim() || !email.trim() || !password) {
      setError(t("admin.field.required"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t("admin.field.invalidEmail"));
      return;
    }
    create.mutate(
      {
        nom: nom.trim(),
        prenom: prenom.trim(),
        email: email.trim(),
        password,
        presets,
        est_proprietaire: owner,
      },
      {
        onSuccess: () => {
          toast.success(t("admin.team.create.done"));
          close();
        },
        onError: (err) => setError(err instanceof ApiError ? err.message : t("admin.toast.error")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={t("admin.team.create.title")}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {t("admin.action.cancel")}
          </Button>
          <Button onClick={submit} loading={create.isPending}>
            {t("admin.team.create.submit")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <div style={{ display: "grid", gap: "var(--nv-s-3)", gridTemplateColumns: "1fr 1fr" }}>
          <FormField label={t("admin.team.create.prenom")} required>
            {(p) => <Input {...p} value={prenom} onChange={(e) => setPrenom(e.target.value)} />}
          </FormField>
          <FormField label={t("admin.team.create.nom")} required>
            {(p) => <Input {...p} value={nom} onChange={(e) => setNom(e.target.value)} />}
          </FormField>
        </div>
        <FormField label={t("admin.team.create.email")} required>
          {(p) => <Input {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />}
        </FormField>
        <FormField label={t("admin.team.create.password")} required>
          {(p) => (
            <Input {...p} type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          )}
        </FormField>
        <FormField label={t("admin.team.create.presets")}>
          {() => <PresetPicker value={presets} onChange={setPresets} disabled={create.isPending} />}
        </FormField>
        <label style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)" }}>
          <input type="checkbox" checked={owner} onChange={(e) => setOwner(e.target.checked)} />
          <span>{t("admin.team.create.owner")}</span>
        </label>
        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--nv-danger, var(--nv-critical))" }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
